import Anchor from "@components/Anchor";
import DatepickerInput from "@components/DatepickerInput";
import FormInput from "@components/FormInput";
import FormError from "@components/FormError";
import FormRow from "@components/FormRow";
import FormSelect from "@components/FormSelect";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";

import { clientRefAtom } from "@contexts/buyAtoms";
import {
	colours,
	device,
	emailRgx,
	filterUrlPath,
	fonts,
	fontsLineHeight,
	isAnchorOrDocumentLink,
	isEmptyContentfulEntry,
	isFelixMobileUrl,
	mobileRgx,
	navigateToUrl,
	richTextHelper,
	useDeviceDetect,
} from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { Actioner, GTMActionKeys } from "@services/actioner";
import { getAwsId } from "@services/getAwsId";
import { reportError } from "@utils/errorReporting";
import { fetchWithTimeout } from "@utils/fetchWithTimeout";
import { useAtom } from "jotai";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { gaEvent } from "src/integrations/gtm";
import styled from "styled-components";

const FormContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  & a {
  }
  @media (max-width: ${device.tablet}) {
    margin-top: 30px;
  }

  @media (min-width: ${device.tablet}) and (max-width: ${device.laptop}) {
    width: 100% !important;
    margin-top: 70px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    padding: 42px 69px 0px 0px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktopM}) {
    margin: 40px 95px 0px 0px;
  }

  @media (min-width: ${device.desktop}) {
    padding: 40px 416px 0px 0px;
  }
`;

const FormBody = styled.form`
  @media (min-width: ${device.tablet}) and (max-width: ${device.laptop}) {
    margin-top: 45px;
  }
`;

const footerTextStyle = `  
  font-size: 12px;

  @media (min-width: ${device.tablet}) and (max-width: ${device.laptop}) {
    font-size: 14px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    font-size: 16px;
  }

  @media (min-width: ${device.desktopS}) {
    font-size: 22px;
  }
`;

const Legend = styled.p`
  ${footerTextStyle}
`;

const Footer = styled(Legend)`
  margin-top: ${(CustomMargin) => (CustomMargin ? "0" : "1rem")};
  margin-left: ${(CustomMargin) => CustomMargin && "8px"};
  max-width: ${(CustomMargin) => CustomMargin && "250px"};

  @media (min-width: ${device.mobileM}) {
    max-width: ${(CustomMargin) => CustomMargin && "300px"};
  }
  @media (min-width: ${device.mobileXL}) {
    max-width: ${(CustomMargin) => CustomMargin && "450px"};
  }
  @media (min-width: ${device.tablet}) {
    max-width: ${(CustomMargin) => CustomMargin && "550px"};
  }
`;

const FooterHyperlink = styled(Anchor)`
  ${footerTextStyle}
  text-decoration: underline;
`;

const ActionButton = styled.button`
  margin-top: 22px;
  align-items: center;
  background-color: ${colours.MIDNIGHT_BLUE};
  border-radius: 12px;
  border: 2px solid ${colours.MIDNIGHT_BLUE};
  box-sizing: border-box;
  color: ${colours.WHITE};
  display: flex;
  font-family: var(--euclid-medium);
  font-size: ${fonts.BUTTON};
  line-height: ${fontsLineHeight.A};
  justify-content: center;
  outline: none;
  padding: 13px 32px;
  min-width: 193px;
  width: 100%;
  transition: 0.2s;
  cursor: pointer;
  
  @media (min-width: 428px) {
    width: fit-content;
  }
  
  :hover {
    border: 2px solid ${colours.WATERMELON};
    background-color: ${colours.WHITE};
    color: ${colours.MIDNIGHT_BLUE};
  }
  
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  :active:enabled {
    transition: 0.3s all;
    transform: translateY(3px);
  }
`;

const MessageContainer = styled.div`
  display: flex;
  height: 100vh;
  align-items: center;
  justify-content: center;
`;

const CompleteMessage = styled.h3`
  align-self: center;
  justify-self: center;
`;

const CheckBoxRow = styled.div`
  width: max-content;
  input {
    height: 24px;
    width: 10%;
  }
`;

const Form = ({
	formType,
	formName,
	footerText,
	openModal,
	submitButtonText = "Submit",
	location,
}) => {
	const { handleSubmit, register, errors, clearErrors, control, reset } =
		useForm({
			mode: "onBlur",
		});

	const pathname = usePathname();

	const { isMobile } = useDeviceDetect();

	const [formStart, setFormStart] = useState(null);

	const [formComplete, setFormComplete] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionError, setSubmissionError] = useState(null);

	const [clientRef] = useAtom(clientRefAtom);

	React.useEffect(() => {
		if (errors && Object.keys(errors).length === 0) {
			return;
		}

		if (formType === "remind-me-later") {
			Actioner.trigger(GTMActionKeys.FORM_ERROR, {
				formName: "set_a_reminder",
				formErrorDetails: JSON.stringify(
					Object.entries(errors).map(([field, { message }]) => ({
						field,
						message,
					})),
				),
			});
		}
		// we may need to look for better solution to use as dependency
	}, [
		formType,
		errors,
		errors.firstName,
		errors.lastName,
		errors.email,
		errors.dob,
	]);

	const internationalizeMoble = (number) => {
		if (!number) return "000000000";
		const cleanNumber = number.replaceAll(" ", "");
		return cleanNumber.startsWith("04")
			? cleanNumber.replace("04", "+614")
			: cleanNumber;
	};

	const remindMeBraze = async (values, e) => {
		await getAwsId(clientRef, values.email);

		Actioner.trigger(Actioner.keyDefs.COMPLETE_REMINDME, { eventData: values });

		if (window?.branch) {
			window.branch.logEvent("COMPLETE_REMINDME");
		}

		if (window?.uetq) {
			window.uetq = window.uetq || [];
			window.uetq.push("event", "COMPLETE_REMINDME", {});
		}
		gaEvent({
			action: "COMPLETE_REMINDME",
			category: "GTM",
			label: "GTM RemindMe form submitted",
			value: "User submitted a form",
		});
	};

	const friendsFamily = async (values, e) => {
		await getAwsId(clientRef, values.email);

		Actioner.trigger(Actioner.keyDefs.COMPLETE_FRIENDS_FAMILY, {
			eventData: values,
		});
		gaEvent({
			action: "COMPLETE_FRIENDS_FAMILY",
			category: "GTM",
			label: "GTM Friends and Family form submitted",
			value: "User submitted a form",
		});
	};
	const leadCollectionForm = async (values, e) => {
		await getAwsId(clientRef, values.email);

		Actioner.trigger(Actioner.keyDefs.COMPLETE_LEAD_COLLECTION_FORM, {
			eventData: values,
		});
		gaEvent({
			action: "COMPLETE_LEAD_COLLECTION_FORM",
			category: "GTM",
			label: "GTM lead capture form submitted",
			value: "User submitted a form",
		});
	};

	const closeWebView = () => {
		window.parent.postMessage(
			{
				action: "collapse_all",
				isWebViewMessage: true,
			},
			"https://wchat.au.freshchat.com",
		);
		setFormComplete(true);
	};

	const closeTab = () => window.close();

	const handleCloseAuthForm = (params) => {
		const { source } = params;
		if (isMobile) {
			//  if the user is from mobile platform
			switch (source) {
				// Facebook Messenger
				case "facebook":
					return navigateToUrl(
						null,
						"https://www.messenger.com/t/felixmobileau",
					);
				// Felix app livechat
				case "mobile":
					return navigateToUrl(null, "https://flx.mobi/chatinappfromweb");
				case "whatsapp":
					return navigateToUrl(
						null,
						"https://api.whatsapp.com/send?phone=+61408598945",
					);
				case "web":
					return closeWebView();
				default:
					return closeWebView();
			}
		}

		//  if the user is from web
		switch (source) {
			case "facebook":
				return closeTab();
			case "web":
				return closeWebView();
			default:
				return closeWebView();
		}
	};

	const identifyUser = async (values) => {
		setIsSubmitting(true);
		setSubmissionError(null);
		const url = location?.href ?? "";
		const params = {};
		const { firstName, lastName, email, dob, mobileNumber } = values;

		url.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
			params[key] = value;
		});

		try {
			await fetchWithTimeout(
				`${process.env.NEXT_PUBLIC_AWS_BASE_URL}/identify`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						fname: firstName,
						lname: lastName,
						email,
						dob,
						phone: mobileNumber,
						user_id: params.fc_user_id,
						conv_id: params.fc_conv_id,
						ticket_id: params.ticket_id,
						source: params.source,
					}),
				},
			);

			handleCloseAuthForm(params);
			return true;
		} catch (error) {
			reportError(error, {
				component: "Form",
				action: "identify_user",
			});
			setSubmissionError(
				"Sorry, there was an error connecting to the server. Please try again.",
			);
			return false;
		} finally {
			setIsSubmitting(false);
		}
	};

	const sendSMS = (values) => {
		if (window?.branch) {
			const { mobileNumber } = values;
			window.branch.data((err, data) => {
				if (
					data?.data_parsed?.felix_app_custom_data &&
					data?.data_parsed?.felix_app_screen_name ===
						"ActivateEmailVerifiedPage"
				) {
					window.branch.sendSMS(
						internationalizeMoble(mobileNumber),
						data?.data_parsed,
						{},

						(err) => {},
					);
				}
			});
		}
	};

	const onFocus = () => {
		if (formType === "remind-me-later" && !formStart) {
			setFormStart(Date.now());
			setTimeout(() => {
				Actioner.trigger(GTMActionKeys.FORM_START, {
					formName: "set_a_reminder",
				});
			}, 1000);
		} else if (formType === "friends-family" && !formStart) {
			setFormStart(Date.now());
			setTimeout(() => {
				Actioner.trigger(GTMActionKeys.FORM_START, {
					formName: "friends_and_family",
				});
			}, 1000);
		} else if (formType === "lead-collection-form" && !formStart) {
			setFormStart(Date.now());
			setTimeout(() => {
				Actioner.trigger(GTMActionKeys.FORM_START, {
					formName: "lead-collection-form",
				});
			}, 1000);
		}
	};

	const onSubmit = (e) => {
		e.preventDefault();
		return handleSubmit(async (values) => {
			if (Object.keys(errors).length === 0) {
				if (formType === "remind-me-later") {
					Actioner.trigger(GTMActionKeys.FORM_SUBMIT_SUCCESS, {
						formName: "set_a_reminder",
						timeToComplete: Math.ceil((Date.now() - formStart) / 1000),
					});
					openModal();
					remindMeBraze(values, e);
				} else if (formType === "friends-family") {
					Actioner.trigger(GTMActionKeys.FORM_SUBMIT_SUCCESS, {
						formName: "friends_family",
						timeToComplete: Math.ceil((Date.now() - formStart) / 1000),
					});
					// openModal();
					friendsFamily(values, e);
				} else if (formType === "lead-collection-form") {
					Actioner.trigger(GTMActionKeys.FORM_SUBMIT_SUCCESS, {
						formName: "lead-collection-form",
						timeToComplete: Math.ceil((Date.now() - formStart) / 1000),
					});
					openModal();
					leadCollectionForm(values, e);
				} else if (formType === "identify-user") {
					const wasIdentified = await identifyUser(values);
					if (!wasIdentified) return;
				} else if (formType === "sms-me-link") {
					openModal();
					sendSMS(values);
				}
			}
			reset();
		})(e);
	};

	const legend = "*required text field";

	const renderLegend = () => <Legend>{legend}</Legend>;

	const renderLegendOption = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => (
				<Footer CustomMargin={formType === "lead-collection-form"}>
					{children}
				</Footer>
			),
			[INLINES.HYPERLINK]: (node, children) =>
				isAnchorOrDocumentLink(node.data.uri) ? (
					<a
						onClick={() =>
							mixpanelTrack("text_link_clicked", {
								cta_position: pathname,
								cta_text: children["0"]["1"],
							})
						}
						href={node.data.uri}
					>
						{children}
					</a>
				) : (
					<Link
						onClick={() =>
							mixpanelTrack("text_link_clicked", {
								cta_position: pathname,
								cta_text: children["0"]["1"],
							})
						}
						href={
							node.data && isFelixMobileUrl(node.data.uri)
								? filterUrlPath(node.data.uri)
								: node.data.uri
						}
					>
						{children}
					</Link>
				),
			[INLINES.ENTRY_HYPERLINK]: (node, children) => (
				<FooterHyperlink
					href={
						node &&
						node.data &&
						node.data.target &&
						node.data.target.fields &&
						node.data.target.fields.relativeUrl &&
						node.data.target.fields.relativeUrl["en-US"] &&
						node.data.target.fields.relativeUrl["en-US"]
					}
				>
					{children}
				</FooterHyperlink>
			),
		},
	};

	const renderFooter = () =>
		!isEmptyContentfulEntry(footerText) && formType !== "lead-collection-form";
	richTextHelper(footerText, renderLegendOption);
	const renderCheckbox = () =>
		!isEmptyContentfulEntry(footerText) &&
		richTextHelper(footerText, renderLegendOption);

	const requiredFieldMessage = "Required field";

	const registerName = register({ required: requiredFieldMessage });
	const registerCheckbox = register({ required: requiredFieldMessage });

	const emailValidationMessage = "Please enter a valid email address";

	const registerEmail = register({
		required: requiredFieldMessage,
		pattern: {
			value: emailRgx,
			message: emailValidationMessage,
		},
	});

	const employeeEmailRgx =
		/[a-zA-Z]+\.[a-zA-Z0-9]+(@vodafone|@tpgtelecom|@lebara)\.com\.au/g;
	const employeeIdRgx = /61\d\d\d\d\d\d/g;

	const registerEmployeeEmail = register({
		required: "*Only TPG Telecom email addresses can be used.",
		pattern: {
			value: employeeEmailRgx,
			message: "Please enter a valid TPG Telecom email address.",
		},
	});

	const registerEmployeeId = register({
		required: "Please enter your Employee ID that starts with 61",
		pattern: {
			value: employeeIdRgx,
			message: "Please enter your Employee ID that starts with 61",
		},
	});

	const registerMobileNumber = register({
		pattern: {
			value: mobileRgx,
			message: "Please enter a valid mobile number",
		},
	});

	const renderNamesRow = () => (
		<FormRow>
			<FormInput
				label="First name"
				name="firstName"
				required
				register={registerName}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
				autoComplete="off"
			/>
			<FormInput
				label="Last name"
				name="lastName"
				required
				register={registerName}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
				autoComplete="off"
			/>
		</FormRow>
	);

	const renderEmailRow = () => (
		<FormRow>
			<FormInput
				label="Email"
				name="email"
				required
				type="email"
				register={registerEmail}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
				autoComplete="off"
			/>
		</FormRow>
	);

	const renderMobileRow = () => (
		<FormRow>
			<FormInput
				label="Mobile Number"
				name="mobileNumber"
				register={registerMobileNumber}
				errors={errors}
				clearErrors={clearErrors}
				autoComplete="off"
			/>
		</FormRow>
	);

	const renderDatepickerRow = (label, name, dateMin, dateMax) => (
		<FormRow>
			<DatepickerInput
				label={label}
				name={name}
				required
				control={control}
				errors={errors}
				dateMin={dateMin}
				dateMax={dateMax}
				onFocus={onFocus}
			/>
		</FormRow>
	);

	const renderEmployeeRow = () => (
		<FormRow>
			<FormInput
				label="First name"
				name="firstName"
				required
				register={registerName}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
			/>
			<FormInput
				label="Last name"
				name="lastName"
				required
				register={registerName}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
			/>
		</FormRow>
	);

	const renderEmployeeEmailRow = () => (
		<FormRow>
			<FormInput
				label="Work email address"
				name="email"
				required
				type="email"
				register={registerEmployeeEmail}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
			/>
		</FormRow>
	);

	const stateTerritoryOptions = [
		{ value: "NSW", label: "NSW" },
		{ value: "ACT", label: "ACT" },
		{ value: "NT", label: "NT" },
		{ value: "QLD", label: "QLD" },
		{ value: "SA", label: "SA" },
		{ value: "TAS", label: "TAS" },
		{ value: "VIC", label: "VIC" },
		{ value: "WA", label: "WA" },
	];

	const renderEmployeeDetailRow = () => (
		<FormRow>
			<FormInput
				label="Employee ID"
				name="employeeId"
				required
				register={registerEmployeeId}
				errors={errors}
				clearErrors={clearErrors}
				onFocus={onFocus}
			/>
			<FormSelect
				label="State"
				name="employeeState"
				options={stateTerritoryOptions}
				placeholder="State"
				required
				register={registerName}
			/>
		</FormRow>
	);

	const renderFormBody = () => {
		switch (formType) {
			case "remind-me-later": {
				const label = "Date for reminder";
				const name = "dateForReminder";
				const dateMin = new Date();
				const currentYear = new Date().getFullYear();
				const dateMax = new Date(new Date().setFullYear(currentYear + 2));
				return (
					<>
						{renderNamesRow()}
						{renderEmailRow()}
						{renderDatepickerRow(label, name, dateMin, dateMax)}
					</>
				);
			}
			case "friends-family": {
				return (
					<>
						<h5>Enter your details</h5>
						<br />
						{renderEmployeeRow()}
						{renderEmployeeEmailRow()}
						{renderEmployeeDetailRow()}
					</>
				);
			}
			case "lead-collection-form": {
				return (
					<>
						<br />
						<FormInput
							name="formName"
							type="text"
							register={registerName}
							errors={errors}
							clearErrors={clearErrors}
							onFocus={onFocus}
							style={{ display: "none" }}
							value={`${formName}`}
						/>
						{renderEmployeeRow()}
						{renderEmailRow()}
						<CheckBoxRow>
							<FormInput
								renderCheckbox={renderCheckbox()}
								name="checkbox"
								type="checkbox"
								required
								register={registerCheckbox}
								errors={errors}
								clearErrors={clearErrors}
								onFocus={onFocus}
							/>
						</CheckBoxRow>
					</>
				);
			}
			case "identify-user": {
				const label = "Date of Birth";
				const name = "dob";
				const dateMax = new Date();
				const currentYear = new Date().getFullYear();
				const dateMin = new Date(new Date().setFullYear(currentYear - 150));
				return (
					<>
						{renderNamesRow()}
						{renderEmailRow()}
						{renderMobileRow()}
						{renderDatepickerRow(label, name, dateMin, dateMax)}
					</>
				);
			}
			case "sms-me-link": {
				return renderMobileRow();
			}
			default:
				return null;
		}
	};

	const renderActionButton = () => (
		<ActionButton type="submit" id="formsubmit" disabled={isSubmitting}>
			{submitButtonText}
		</ActionButton>
	);

	const renderFormCompleteMessage = () => {
		switch (formType) {
			case "identify-user":
				return (
					<CompleteMessage>
						Please click back button on the top left corner to go back to chat
					</CompleteMessage>
				);
			default:
				return null;
		}
	};

	return formComplete ? (
		<MessageContainer>{renderFormCompleteMessage()}</MessageContainer>
	) : (
		<>
			<FormContainer>
				<FormBody
					onSubmit={onSubmit}
					id={`form-${formType}`}
					autoComplete="off"
				>
					{renderFormBody()}
					{formType !== "sms-me-link" && renderLegend()}
					{renderFooter()}
					{submissionError && <FormError role="alert">{submissionError}</FormError>}
					{renderActionButton()}
				</FormBody>
			</FormContainer>
		</>
	);
};

export default Form;
